import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Mic, Square, Play, Pause, Trash2, Loader2, Volume2, Sparkles, X, FileText, Save } from "lucide-react";

/**
 * Per-step voice controls for the Teach editor. A step can have one of:
 *   voice_source: "ai" (default) — pick provider + voice, preview & play body
 *   voice_source: "recording"    — record with MediaRecorder + teleprompter modal,
 *                                  stored via Emergent Object Storage (audio_note_id)
 *
 * Fields written back onto the step object:
 *   { voice_source, voice_provider, voice_id, audio_note_id }
 */
export function StepVoicePicker({ step, onChange, bodyText, testid, defaultVoice }) {
  const source   = step.voice_source   || "ai";
  // When the lesson is language-tagged (e.g. Spanish), defaultVoice pins the
  // provider+voice unless the step has explicitly picked otherwise.
  const provider = step.voice_provider || (defaultVoice?.provider) || "openai";
  const voice    = step.voice_id       || (defaultVoice?.id) || (provider === "openai" ? "nova" : "Kore");
  const isAuto   = !!defaultVoice && !step.voice_id;
  const noteId   = step.audio_note_id  || null;

  const [voices, setVoices] = useState({ providers: [], sample_text: "" });
  const [busy, setBusy]     = useState(null); // 'sample' | 'body' | 'save' | 'note'
  const [playing, setPlaying]     = useState(false);
  const [scriptOpen, setScriptOpen] = useState(false);

  const audioRef    = useRef(null);
  const currentUrl  = useRef(null); // active blob: URL to revoke on unmount

  useEffect(() => {
    api.ttsVoices().then(setVoices).catch(() => {});
    return () => {
      if (currentUrl.current) URL.revokeObjectURL(currentUrl.current);
    };
  }, []);

  const providers    = voices.providers || [];
  const providerCfg  = providers.find((p) => p.id === provider) || providers[0];
  const voiceList    = providerCfg?.voices || [];

  const playBlob = async (blob) => {
    if (currentUrl.current) URL.revokeObjectURL(currentUrl.current);
    const url = URL.createObjectURL(blob);
    currentUrl.current = url;
    if (audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.onended = () => setPlaying(false);
      await audioRef.current.play();
      setPlaying(true);
    }
  };

  const preview = async (kind /* 'sample' | 'body' */) => {
    if (kind === "body" && !(bodyText || "").trim()) {
      toast.error("Write the body text first, then click Play.");
      return;
    }
    setBusy(kind);
    try {
      const blob = await api.ttsPreview({
        provider, voice,
        text: kind === "body" ? bodyText : "",
        use_sample: kind === "sample",
      });
      await playBlob(blob);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(null); }
  };

  const chooseRecordingMode = () => {
    onChange({ ...step, voice_source: "recording" });
    setScriptOpen(true);
  };

  const playRecording = async () => {
    if (!noteId) return;
    if (audioRef.current && playing) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    setBusy("note");
    try {
      const blob = await api.fetchVoiceNoteBlob(noteId);
      await playBlob(blob);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(null); }
  };

  const removeRecording = async () => {
    if (noteId) { try { await api.deleteVoiceNote(noteId); } catch { /* non-fatal */ } }
    onChange({ ...step, voice_source: "ai", audio_note_id: null });
    if (audioRef.current) audioRef.current.pause();
    setPlaying(false);
  };

  const onSavedNote = (newId) => {
    onChange({ ...step, voice_source: "recording", audio_note_id: newId });
  };

  return (
    <div className="mt-3 rounded-2xl border border-[#E5DECF] bg-white/60 p-3" data-testid={testid || "step-voice-picker"}>
      <div className="flex items-center gap-2 text-xs font-bold text-[#6B7280]">
        <Volume2 size={13} className="text-[#0D9488]" /> Audio for this step
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => onChange({ ...step, voice_source: "ai" })}
          data-testid={`${testid || "voice"}-source-ai`}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
            source === "ai" ? "bg-[#0D9488] text-white" : "bg-[#F2ECE1] text-[#6B7280] hover:bg-[#E5DECF]"
          }`}
        >
          <Sparkles size={12} /> AI voice
        </button>
        <button
          type="button"
          onClick={chooseRecordingMode}
          data-testid={`${testid || "voice"}-source-recording`}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
            source === "recording" ? "bg-[#7A3FF2] text-white" : "bg-[#F2ECE1] text-[#6B7280] hover:bg-[#E5DECF]"
          }`}
        >
          <Mic size={12} /> My recording
        </button>
      </div>

      {source === "ai" && (
        <>
          <div className="mt-3 grid gap-2 sm:grid-cols-[auto_1fr_auto_auto]">
            <select
              value={provider}
              onChange={(e) => {
                const p = e.target.value;
                const cfg = providers.find((x) => x.id === p);
                const first = cfg?.voices?.[0]?.id || voice;
                onChange({ ...step, voice_provider: p, voice_id: first });
              }}
              data-testid={`${testid || "voice"}-provider`}
              className="rounded-xl border border-[#E5DECF] bg-white px-3 py-2 text-sm font-semibold text-[#1F2937]"
            >
              {providers.map((p) => (
                <option key={p.id} value={p.id} disabled={!p.configured}>
                  {p.label}{!p.configured ? " (no API key)" : ""}
                </option>
              ))}
            </select>
            <select
              value={voice}
              onChange={(e) => onChange({ ...step, voice_provider: provider, voice_id: e.target.value })}
              data-testid={`${testid || "voice"}-voice`}
              className="rounded-xl border border-[#E5DECF] bg-white px-3 py-2 text-sm font-semibold text-[#1F2937]"
            >
              {voiceList.map((v) => (
                <option key={v.id} value={v.id}>{v.label}</option>
              ))}
            </select>
            <button
              type="button" onClick={() => preview("sample")} disabled={!!busy}
              data-testid={`${testid || "voice"}-preview-sample`}
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#E5DECF] px-3 py-2 text-xs font-bold text-[#0B7F74] hover:bg-[#17BEBB]/10 disabled:opacity-50"
            >
              {busy === "sample" ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Preview
            </button>
            <button
              type="button" onClick={() => preview("body")} disabled={!!busy}
              data-testid={`${testid || "voice"}-play-body`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0D9488] px-3 py-2 text-xs font-bold text-white hover:bg-[#0B7F74] disabled:opacity-50"
            >
              {busy === "body" ? <Loader2 size={13} className="animate-spin" /> : <Volume2 size={13} />} Play this step
            </button>
          </div>
          {isAuto && (
            <div
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#DB2777]/10 px-2.5 py-1 text-[11px] font-bold text-[#BE185D]"
              data-testid={`${testid || "voice"}-auto-badge`}
            >
              <Sparkles size={11} /> Auto-picked for this language ({defaultVoice.label || defaultVoice.id})
            </div>
          )}
        </>
      )}

      {source === "recording" && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {noteId ? (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#10B981]/15 px-3 py-1.5 text-xs font-bold text-[#047857]">
                <Mic size={12} /> Recording saved
              </span>
              <button
                type="button" onClick={playRecording} disabled={!!busy}
                data-testid={`${testid || "voice"}-play-recording`}
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#E5DECF] px-3 py-1.5 text-xs font-bold text-[#0B7F74] hover:bg-[#17BEBB]/10 disabled:opacity-50"
              >
                {busy === "note" ? <Loader2 size={13} className="animate-spin" /> : (playing ? <Pause size={13} /> : <Play size={13} />)}
                {playing ? " Pause" : " Play"}
              </button>
              <button
                type="button" onClick={() => setScriptOpen(true)}
                data-testid={`${testid || "voice"}-show-script`}
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#E5DECF] px-3 py-1.5 text-xs font-bold text-[#6B7280] hover:bg-[#F2ECE1]"
              >
                <FileText size={13} /> Show script
              </button>
              <button
                type="button" onClick={() => setScriptOpen(true)}
                data-testid={`${testid || "voice"}-rerecord`}
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#E5DECF] px-3 py-1.5 text-xs font-bold text-[#6B7280] hover:bg-[#F2ECE1]"
              >
                <Mic size={13} /> Re-record
              </button>
              <button
                type="button" onClick={removeRecording}
                data-testid={`${testid || "voice"}-remove`}
                className="inline-flex items-center gap-1 text-xs font-bold text-[#E05A47] hover:underline"
              >
                <Trash2 size={12} /> Remove
              </button>
            </>
          ) : (
            <button
              type="button" onClick={() => setScriptOpen(true)}
              data-testid={`${testid || "voice"}-open-script`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#7A3FF2] px-4 py-2 text-xs font-bold text-white hover:bg-[#652BDB]"
            >
              <FileText size={13} /> Open script &amp; record
            </button>
          )}
        </div>
      )}

      {scriptOpen && (
        <RecordScriptModal
          initialText={bodyText || ""}
          hasExisting={!!noteId}
          existingId={noteId}
          onClose={() => setScriptOpen(false)}
          onSaved={(id) => { onSavedNote(id); setScriptOpen(false); }}
          testid={`${testid || "voice"}-modal`}
        />
      )}

      <audio ref={audioRef} className="hidden" />
    </div>
  );
}

/**
 * Teleprompter + recorder modal.
 * Editable script (pre-filled from the step body) with drag-handle resize.
 * Flow: Record → Stop → Preview → Save recording to lesson → close.
 */
function RecordScriptModal({ initialText, hasExisting, existingId, onClose, onSaved, testid }) {
  const [scriptText, setScriptText] = useState(initialText || "");
  const [phase, setPhase] = useState("ready"); // 'ready' | 'recording' | 'review' | 'uploading'
  const [seconds, setSeconds] = useState(0);
  const [pendingUrl, setPendingUrl] = useState(null);

  const mediaRef  = useRef(null);
  const chunksRef = useRef([]);
  const timerRef  = useRef(null);
  const streamRef = useRef(null);
  const blobRef   = useRef(null);

  // Lock body scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
      if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        blobRef.current = blob;
        if (pendingUrl) URL.revokeObjectURL(pendingUrl);
        setPendingUrl(URL.createObjectURL(blob));
        setPhase("review");
      };
      mediaRef.current = rec;
      rec.start();
      setPhase("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      toast.error("Couldn't access microphone. Please allow mic permissions.");
    }
  };

  const stop = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
  };

  const save = async () => {
    if (!blobRef.current) return;
    setPhase("uploading");
    try {
      if (hasExisting && existingId) { try { await api.deleteVoiceNote(existingId); } catch { /* non-fatal */ } }
      const { id } = await api.uploadVoiceNote(blobRef.current, "recording.webm");
      toast.success("Recording saved to lesson");
      onSaved(id);
    } catch (e) {
      toast.error(formatApiError(e));
      setPhase("review");
    }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-3 sm:p-6"
      data-testid={testid}
      onClick={(e) => { if (e.target === e.currentTarget && phase !== "recording") onClose(); }}
    >
      <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-[#E5DECF] bg-[#FAF7F2] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#E5DECF] bg-white px-5 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#7A3FF2]/10 text-[#7A3FF2]"><Mic size={18} /></div>
            <div>
              <div className="font-heading text-base font-extrabold text-[#1F2937]">Record your voice</div>
              <div className="font-body text-xs text-[#6B7280]">Read the script below aloud. Drag the corner to make the text bigger.</div>
            </div>
          </div>
          <button
            onClick={onClose} disabled={phase === "recording" || phase === "uploading"}
            data-testid={`${testid}-close`} aria-label="Close"
            className="rounded-xl p-2 text-[#1F2937] hover:bg-[#F2ECE1] disabled:opacity-40"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <label className="block text-sm font-bold text-[#1F2937]">
            Script <span className="font-normal text-[#94A3B8]">(edit for how you'd like to read it)</span>
          </label>
          <textarea
            value={scriptText}
            onChange={(e) => setScriptText(e.target.value)}
            data-testid={`${testid}-script`}
            className="mt-2 min-h-[180px] w-full resize-y rounded-2xl border-2 border-[#E5DECF] bg-white p-4 font-body text-lg leading-relaxed text-[#1F2937] outline-none focus:border-[#7A3FF2]"
            placeholder="What you'll read aloud…"
            readOnly={phase === "recording" || phase === "uploading"}
          />

          {phase === "recording" && (
            <div className="mt-4 flex items-center gap-3 rounded-2xl bg-[#E05A47]/10 px-4 py-3">
              <span className="relative flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E05A47] opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-[#E05A47]" />
              </span>
              <span className="font-heading text-sm font-extrabold text-[#B23125]">Recording…</span>
              <span className="font-heading text-sm font-bold text-[#B23125]" data-testid={`${testid}-timer`}>{fmt(seconds)}</span>
            </div>
          )}

          {phase === "review" && pendingUrl && (
            <div className="mt-4 rounded-2xl border border-[#E5DECF] bg-white p-4">
              <div className="text-xs font-bold text-[#6B7280]">Preview your recording</div>
              <audio controls src={pendingUrl} className="mt-2 w-full" data-testid={`${testid}-preview`} />
              <div className="mt-1 font-body text-xs text-[#94A3B8]">Not saved yet — hit "Save recording to lesson" below.</div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[#E5DECF] bg-white px-5 py-3">
          {phase === "ready" && (
            <>
              <button
                onClick={onClose}
                data-testid={`${testid}-cancel`}
                className="rounded-full bg-white border border-[#E5DECF] px-4 py-2 font-heading text-sm font-bold text-[#6B7280] hover:bg-[#F2ECE1]"
              >
                Cancel
              </button>
              <button
                onClick={start}
                data-testid={`${testid}-record`}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#7A3FF2] px-5 py-2 font-heading text-sm font-bold text-white shadow-sm hover:bg-[#652BDB]"
              >
                <Mic size={14} /> Start recording
              </button>
            </>
          )}
          {phase === "recording" && (
            <button
              onClick={stop}
              data-testid={`${testid}-stop`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#E05A47] px-5 py-2 font-heading text-sm font-bold text-white shadow-sm hover:brightness-95"
            >
              <Square size={14} /> Stop recording
            </button>
          )}
          {phase === "review" && (
            <>
              <button
                onClick={() => { blobRef.current = null; if (pendingUrl) URL.revokeObjectURL(pendingUrl); setPendingUrl(null); setPhase("ready"); }}
                data-testid={`${testid}-retake`}
                className="rounded-full bg-white border border-[#E5DECF] px-4 py-2 font-heading text-sm font-bold text-[#6B7280] hover:bg-[#F2ECE1]"
              >
                Retake
              </button>
              <button
                onClick={save}
                data-testid={`${testid}-save`}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#10B981] px-5 py-2 font-heading text-sm font-bold text-white shadow-sm hover:bg-[#0E9E6E]"
              >
                <Save size={14} /> Save recording to lesson
              </button>
            </>
          )}
          {phase === "uploading" && (
            <span className="inline-flex items-center gap-2 rounded-full bg-[#F2ECE1] px-4 py-2 text-sm font-bold text-[#6B7280]">
              <Loader2 size={14} className="animate-spin" /> Uploading…
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default StepVoicePicker;
